// packages/web/src/pages/CourtDetailPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Link as MuiLink,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MoneyIcon from '@mui/icons-material/Money';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import WebsiteIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import MapIcon from '@mui/icons-material/Map';
import PaymentIcon from '@mui/icons-material/Payment';
import ReportIcon from '@mui/icons-material/Report';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { NewCourtReportDialog } from '../components/Courts/NewCourtReportDialog';
import { useAuth } from '../hooks/useAuth';
import { Court, CourtService } from '@matchpoint/shared';

export const CourtDetailPage: React.FC = () => {
  const { courtId } = useParams<{ courtId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, signIn, signUp, signOut } = useAuth();
  const [court, setCourt] = useState<Court | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  useEffect(() => {
    const fetchCourt = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!courtId) {
          setError('Invalid court ID');
          setLoading(false);
          return;
        }
        
        const courtService = CourtService.getInstance();
        await courtService['firebaseService'].initializeApp();
        
        const courtData = await courtService.getCourt(courtId);
        
        if (courtData) {
          setCourt(courtData);
        } else {
          setError('Court not found');
        }
      } catch (err) {
        console.error('Error loading court:', err);
        setError('Failed to load court details');
      } finally {
        setLoading(false);
      }
    };

    fetchCourt();
  }, [courtId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleSignIn = async (email: string, password: string) => {
    await signIn(email, password);
  };

  const handleSignUp = async (email: string, password: string, name: string) => {
    await signUp(email, password, name);
  };

  const handleReportClick = () => {
    if (!user) {
      setLoginDialogOpen(true);
      return;
    }
    setReportDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
        <Container maxWidth="lg" sx={{ flexGrow: 1, py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Container>
        <Footer />
      </Box>
    );
  }

  if (error || !court) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
        <Container maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Court not found'}
          </Alert>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Go Back
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
      
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: { xs: 2, sm: 4 } }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: { xs: 2, sm: 3 } }}
          size={isMobile ? "small" : "medium"}
        >
          Back
        </Button>

        <Grid container spacing={{ xs: 2, sm: 4 }}>
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Court Header */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, flex: 1, pr: 1 }}>
                    {court.name}
                  </Typography>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    {court.pricing?.hourly ? (
                      <>
                        <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                          ${court.pricing.hourly}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          per hour
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="h6" color="text.secondary" fontStyle="italic" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                        Free
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReportIcon />}
                  onClick={handleReportClick}
                  sx={{ fontSize: '0.7rem', py: 0.5, px: 1, mb: 2 }}
                >
                  Suggest Edit
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <LocationOnIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {court.location.address}
                    {court.location.city && `, ${court.location.city}`}
                    {court.location.country && `, ${court.location.country}`}
                  </Typography>
                </Box>
                
                {/* Facility chips */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                  {court.details?.facility_type && (
                    <Chip 
                      label={court.details.facility_type} 
                      color="primary" 
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  )}
                  {court.details?.court_surface && (
                    <Chip 
                      label={court.details.court_surface} 
                      color="secondary" 
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  )}
                  {court.details?.lights && (
                    <Chip 
                      icon={<LightbulbIcon sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }} />}
                      label="Lights" 
                      color="success" 
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  )}
                  {court.details?.indoor_courts && court.details.indoor_courts > 0 && (
                    <Chip 
                      icon={<HomeIcon sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }} />}
                      label={`${court.details.indoor_courts} Indoor`} 
                      color="info" 
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Court Statistics */}
              {(court.details?.total_courts || court.details?.indoor_courts !== undefined) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    <InfoIcon fontSize="small" />
                    Court Information
                  </Typography>
                  <Grid container spacing={1.5}>
                    {court.details?.total_courts && (
                      <Grid item xs={6} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="h5" color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            {court.details.total_courts}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Total Courts
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {court.details?.indoor_courts !== undefined && (
                      <Grid item xs={6} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="h5" color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            {court.details.indoor_courts}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Indoor
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {court.details?.indoor_courts !== undefined && court.details?.total_courts !== undefined && (
                      <Grid item xs={6} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="h5" color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            {`${court.details.total_courts - (court.details.indoor_courts || 0)}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Outdoor
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Contact Information */}
              {(court.details?.telephone || court.details?.website || court.details?.google_maps_url) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      <PhoneIcon fontSize="small" />
                      Contact
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {court.details?.telephone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <MuiLink href={`tel:${court.details.telephone}`} underline="none" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            {court.details.telephone}
                          </MuiLink>
                        </Box>
                      )}
                      {court.details?.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WebsiteIcon fontSize="small" color="action" />
                          <MuiLink href={court.details.website} target="_blank" rel="noopener noreferrer" underline="none" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            Visit Website
                          </MuiLink>
                        </Box>
                      )}
                      {court.details?.google_maps_url && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MapIcon fontSize="small" color="action" />
                          <MuiLink href={court.details.google_maps_url} target="_blank" rel="noopener noreferrer" underline="none" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            View on Google Maps
                          </MuiLink>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              {/* Amenities */}
              {court.amenities && court.amenities.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      <SportsTennisIcon fontSize="small" />
                      Amenities
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {court.amenities.map((amenity, index) => (
                        <Chip 
                          key={index}
                          label={amenity}
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {/* Operating Hours */}
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  <AccessTimeIcon fontSize="small" />
                  Hours
                </Typography>
                <List dense disablePadding>
                  {Object.entries(court.availability.schedule).map(([day, hours]) => (
                    <ListItem key={day} sx={{ px: 0, py: 0.5 }}>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {day}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {hours.open} - {hours.close}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Quick Info
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LocationOnIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={<Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Location</Typography>}
                    secondary={<Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{`${court.location.city}${court.location.country ? `, ${court.location.country}` : ''}`}</Typography>}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <MoneyIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={<Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Rate</Typography>}
                    secondary={
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {court.pricing?.hourly ? `$${court.pricing.hourly}/hour` : 'Free'}
                      </Typography>
                    }
                  />
                </ListItem>
                {court.details?.facility_type && (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <BusinessIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Type</Typography>}
                      secondary={<Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{court.details.facility_type}</Typography>}
                    />
                  </ListItem>
                )}
                {court.details?.court_surface && (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <SportsTennisIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Surface</Typography>}
                      secondary={<Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{court.details.court_surface}</Typography>}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      
      <Footer />

  <LoginDialog
    open={loginDialogOpen}
    onClose={() => setLoginDialogOpen(false)}
  />

      {court && user && (
        <NewCourtReportDialog
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          user={user}
        />
      )}
    </Box>
  );
};

export default CourtDetailPage;